"use client";

import { handleButtonTracking } from "@/app/utils/button-tracking";
import { handleCheckoutSession } from "@/app/utils/checkout-session";
import { sans, mono } from "@/app/utils/fonts";
import { DISCOUNT_ID, PRICING_ID_T1, PRICING_NAME_T1 } from "@/lib/constants";
import { AvatarRating } from "./AvatarRating";

const EDITORS = ["Cursor", "Claude Code", "Copilot", "Windsurf", "Codex"];

const BUILD_STEPS = [
  "Scaffolding side-panel UI (React + WXT)",
  "Wiring Stripe checkout + entitlement gate",
  "Adding chrome.identity OAuth",
  "Configuring Manifest V3 permissions (least-privilege)",
  "Verifying:  tsc --noEmit  ·  wxt build",
];

// Done-for-you capabilities, shown as inline pills in the subhead.
const PILLS = [
  {
    label: "payments",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5">
        <rect width="20" height="14" x="2" y="5" rx="2" />
        <line x1="2" x2="22" y1="10" y2="10" />
      </svg>
    ),
  },
  {
    label: "accounts",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5">
        <path d="M18 20a6 6 0 0 0-12 0" />
        <circle cx="12" cy="10" r="4" />
        <circle cx="12" cy="12" r="10" />
      </svg>
    ),
  },
  {
    label: "store setup",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5">
        <path d="m7.5 4.27 9 5.15" />
        <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" />
        <path d="m3.3 7 8.7 5 8.7-5" />
        <path d="M12 22V12" />
      </svg>
    ),
  },
];

export default function Hero() {
  return (
    <header className="relative overflow-hidden bg-ink">
      {/* Atmosphere */}
      <div className="pointer-events-none absolute inset-0 -z-10">
        {/* fine dot grid, faded toward center-top */}
        <div className="bg-dot-grid absolute inset-0 [mask-image:radial-gradient(1100px_circle_at_50%_16%,black,transparent_75%)]" />
        {/* warm vermilion glow bleeding from the top edge */}
        <div className="absolute left-1/2 top-[-22%] h-[520px] w-[1000px] -translate-x-1/2 rounded-full bg-[radial-gradient(ellipse_at_center,rgba(11,99,222,0.28),transparent_68%)] blur-[80px]" />
        <div className="absolute left-1/2 top-[-6%] h-[300px] w-[620px] -translate-x-1/2 rounded-full bg-[radial-gradient(ellipse_at_center,rgba(11,99,222,0.14),transparent_70%)] blur-2xl" />
        {/* settle into the page base */}
        <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-b from-transparent to-ink" />
      </div>

      <div className="mx-auto flex max-w-6xl flex-col items-center px-6 pb-20 pt-32 text-center sm:pb-24 sm:pt-40 lg:pt-52">
        {/* Eyebrow badge */}
        <div className="mb-9 inline-flex items-center gap-2 rounded-full border border-line bg-panel/70 px-3.5 py-1.5 text-sm text-zinc-300 shadow-[0_1px_0_rgba(255,255,255,0.04)_inset] backdrop-blur">
          <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-75" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-primary" />
          </span>
          The AI-native extension starter
          <span className="text-zinc-600">·</span>
          <span className="font-medium text-zinc-100">Chrome · Firefox · Edge</span>
        </div>

        {/* Headline */}
        <h1
          className={`${sans.className} max-w-5xl text-balance text-5xl font-medium leading-[0.98] tracking-tight text-white sm:text-6xl lg:text-[5.25rem]`}
        >
          Ship a Chrome extension
          <br className="hidden sm:block" /> that{" "}
          <span className="bg-gradient-to-br from-[#4d94ff] via-primary to-[#0a4fb0] bg-clip-text text-transparent">
            makes money
          </span>
        </h1>

        {/* Subhead with inline capability pills */}
        <p className="mt-8 max-w-2xl text-pretty text-lg leading-relaxed text-zinc-400 lg:text-xl">
          Your Cursor, Claude, or Copilot builds right on top of it, with{" "}
          {PILLS.map((p, i) => (
            <span key={p.label}>
              <span className="mx-0.5 inline-flex translate-y-[2px] items-center gap-1.5 rounded-md border border-line bg-panel/80 px-2 py-0.5 text-[0.95em] font-medium text-zinc-200">
                <span className="text-primary">{p.icon}</span>
                {p.label}
              </span>
              {i < PILLS.length - 1 ? (i === PILLS.length - 2 ? " and " : ", ") : " "}
            </span>
          ))}
          already done. Launch in days, not weeks.
        </p>

        {/* CTAs */}
        <div className="mt-10 flex w-full flex-col items-center justify-center gap-4 sm:flex-row">
          <button
            onClick={() =>
              handleCheckoutSession(
                PRICING_ID_T1,
                DISCOUNT_ID,
                PRICING_NAME_T1,
                "hero",
              )
            }
            className="group inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-primary px-8 py-4 text-base font-semibold text-white shadow-[0_10px_40px_-8px_rgba(11,99,222,0.6)] ring-1 ring-inset ring-white/15 transition-all duration-200 hover:bg-secondary hover:shadow-[0_14px_48px_-6px_rgba(11,99,222,0.7)] active:scale-[0.98] sm:w-auto"
          >
            Get ExtensionFast
            <span className="text-sm text-white/55 line-through">$249</span>
            <span className="text-sm font-bold">$149</span>
          </button>

          <button
            onClick={() => {
              handleButtonTracking("clicked_see_how_it_works");
              document
                .getElementById("works")
                ?.scrollIntoView({ behavior: "smooth" });
            }}
            className="group inline-flex w-full items-center justify-center gap-2 rounded-full border border-line bg-panel/50 px-7 py-4 text-base font-medium text-zinc-200 backdrop-blur transition-all duration-200 hover:border-zinc-600 hover:bg-panel-light active:scale-[0.98] sm:w-auto"
          >
            See how it works
            <span className="transition-transform group-hover:translate-y-0.5">
              ↓
            </span>
          </button>
        </div>

        {/* Trust */}
        <div className="mt-9 flex flex-col items-center gap-3">
          <AvatarRating />
          <p className="flex items-center gap-1.5 text-sm text-zinc-500">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="text-primary"
            >
              <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
              <path d="m9 12 2 2 4-4" />
            </svg>
            30-day money-back guarantee
          </p>
        </div>

        {/* Showpiece: the agent terminal */}
        <div className="relative mt-24 w-full max-w-4xl">
          <div className="absolute -inset-6 -z-10 rounded-[2rem] bg-[radial-gradient(ellipse_at_center,rgba(11,99,222,0.22),transparent_70%)] blur-3xl" />
          <div className="overflow-hidden rounded-2xl border border-line bg-ink-deep text-left shadow-2xl ring-1 ring-white/5">
            {/* title bar */}
            <div className="flex items-center gap-2 border-b border-line bg-white/[0.02] px-4 py-3">
              <span className="h-3 w-3 rounded-full bg-[#ff5f57]" />
              <span className="h-3 w-3 rounded-full bg-[#febc2e]" />
              <span className="h-3 w-3 rounded-full bg-[#28c840]" />
              <span className={`${mono.className} ml-3 text-xs text-zinc-500`}>
                your-agent · extensionfast
              </span>
              <span
                className={`${mono.className} ml-auto hidden text-xs text-zinc-500 sm:block`}
              >
                Chrome · Firefox · Edge
              </span>
            </div>

            {/* body */}
            <div
              className={`${mono.className} space-y-2 px-5 py-5 text-[13px] leading-relaxed sm:text-sm`}
            >
              <p className="text-zinc-500">
                <span className="text-primary">▸</span> prompt
              </p>
              <p className="text-zinc-200">
                &ldquo;Add a paywalled feature to my ExtensionFast
                extension&rdquo;
              </p>
              <div className="h-2" />
              {BUILD_STEPS.map((step) => (
                <p key={step} className="flex items-start gap-2 text-zinc-300">
                  <span className="mt-0.5 text-[#28c840]">✓</span>
                  <span>{step}</span>
                </p>
              ))}
              <p className="flex items-center gap-2 pt-2 font-semibold text-white">
                <span className="text-primary">→</span> Ready to load in Chrome.
                <span className="ml-0.5 inline-block h-4 w-2 animate-pulse bg-primary/80" />
              </p>
            </div>
          </div>
        </div>

        {/* Builds with */}
        <p className="mt-8 text-sm text-zinc-500">
          Builds with{" "}
          <span className={`${mono.className} text-zinc-400`}>
            {EDITORS.join(" · ")}
          </span>
        </p>
      </div>
    </header>
  );
}
